/**
 * Hook for the user's default clinical-note templates.
 *
 * Defaults are persisted server-side on the auth-scoped transcriber_db user
 * profile (via the `/api/templates/defaults` BFF endpoints), so they survive
 * org switches, delegation, and device changes. Uses React Query for caching
 * and deduplication across the templates page, dictation, conversation, and
 * the session smart panel.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export type TemplateDefaultKind = 'conversation' | 'dictation';

interface TemplateDefaultsResponse {
  default_dictation_template_id: number | null;
  default_template_id: number | null;
}

interface TemplateDefaults {
  dictation: number | null;
  conversation: number | null;
  isLoading: boolean;
  setTemplateDefault: (
    kind: TemplateDefaultKind,
    templateId: number | null,
    callbacks?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
}

export const TEMPLATE_DEFAULTS_QUERY_KEY = ['template-defaults'];

// Legacy localStorage keys, migrated once to the server then removed.
const LEGACY_KEYS: Record<TemplateDefaultKind, string> = {
  conversation: 'defaultConversationTemplateId',
  dictation: 'defaultDictationTemplateId',
};

/** Which server field holds each kind's default; the embed reads the same fields. */
export const TEMPLATE_DEFAULT_FIELD: Record<TemplateDefaultKind, keyof TemplateDefaultsResponse> = {
  conversation: 'default_template_id',
  dictation: 'default_dictation_template_id',
};

// Guard so the one-time migration runs at most once per app session, no
// matter how many components mount the hook.
let migrationAttempted = false;

const readLegacyDefault = (kind: TemplateDefaultKind): number | null => {
  try {
    const stored = localStorage.getItem(LEGACY_KEYS[kind]);
    if (!stored) return null;
    const parsed = Number.parseInt(stored, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const clearLegacyDefault = (kind: TemplateDefaultKind): void => {
  try {
    localStorage.removeItem(LEGACY_KEYS[kind]);
  } catch {
    // localStorage unavailable — nothing to migrate away.
  }
};

const fetchTemplateDefaults = async (): Promise<TemplateDefaultsResponse> => {
  const res = await fetch('/api/templates/defaults', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to fetch template defaults');
  return res.json();
};

export const useTemplateDefaults = (): TemplateDefaults => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: TEMPLATE_DEFAULTS_QUERY_KEY,
    queryFn: fetchTemplateDefaults,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async ({ kind, templateId }: { kind: TemplateDefaultKind; templateId: number | null }) => {
      const res = await fetch('/api/templates/defaults', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [TEMPLATE_DEFAULT_FIELD[kind]]: templateId }),
      });
      if (!res.ok) throw new Error('Failed to update template default');
    },
    onMutate: async ({ kind, templateId }) => {
      await queryClient.cancelQueries({ queryKey: TEMPLATE_DEFAULTS_QUERY_KEY });
      const previous = queryClient.getQueryData<TemplateDefaultsResponse>(TEMPLATE_DEFAULTS_QUERY_KEY);
      queryClient.setQueryData<TemplateDefaultsResponse>(TEMPLATE_DEFAULTS_QUERY_KEY, (old) => ({
        default_dictation_template_id: old?.default_dictation_template_id ?? null,
        default_template_id: old?.default_template_id ?? null,
        [TEMPLATE_DEFAULT_FIELD[kind]]: templateId,
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(TEMPLATE_DEFAULTS_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_DEFAULTS_QUERY_KEY });
    },
  });

  const setTemplateDefault = (
    kind: TemplateDefaultKind,
    templateId: number | null,
    callbacks?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    mutation.mutate({ kind, templateId }, callbacks);
  };

  // One-time migration: push any legacy localStorage default that the server
  // doesn't already have. The local copy is only dropped once the server write
  // is confirmed — a failed PUT keeps the migration source for the next
  // session instead of silently losing the preference from both stores.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot migration keyed on data; mutation is stable
  useEffect(() => {
    if (migrationAttempted || !data) return;
    migrationAttempted = true;

    for (const kind of ['dictation', 'conversation'] as TemplateDefaultKind[]) {
      const serverValue = data[TEMPLATE_DEFAULT_FIELD[kind]];
      const legacyValue = readLegacyDefault(kind);
      if (serverValue == null && legacyValue != null) {
        mutation.mutate({ kind, templateId: legacyValue }, { onSuccess: () => clearLegacyDefault(kind) });
      } else {
        clearLegacyDefault(kind);
      }
    }
  }, [data]);

  return {
    dictation: data?.default_dictation_template_id ?? null,
    conversation: data?.default_template_id ?? null,
    isLoading,
    setTemplateDefault,
  };
};
